/**
 * PortalApp — self-contained router tree for the patient portal.
 * Mounted at `/portal/*` by App.js *outside* the staff AuthProvider so
 * patient auth cannot collide with staff auth.
 */
import { useEffect, useLayoutEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '../components/ui/sonner';

import { PortalAuthProvider, usePortalAuth } from './PortalAuthContext';
import { PortalThemeProvider } from './PortalThemeContext';
import PortalLayout from './PortalLayout';
import PortalLoginPage from './pages/PortalLoginPage';
import PortalDashboardPage from './pages/PortalDashboardPage';
import PortalAccountPage from './pages/PortalAccountPage';
import PortalProfilePage from './pages/PortalProfilePage';
import PortalPrescriptionsPage from './pages/PortalPrescriptionsPage';
import PortalRefillsPage from './pages/PortalRefillsPage';
import PortalOrdersPage from './pages/PortalOrdersPage';
import PortalShopPage from './pages/PortalShopPage';
import PortalTrackersPage from './pages/PortalTrackersPage';
import './portalCare.css';
import './portalFoundation.css';
import PortalAIPage from './pages/PortalAIPage';
import PortalChatPage from './pages/PortalChatPage';
import PortalRemindersPage from './pages/PortalRemindersPage';
import PortalAdverseReactionsPage from './pages/PortalAdverseReactionsPage';
import PortalLoyaltyPage from './pages/PortalLoyaltyPage';
import { installHeadTags, registerPortalServiceWorker, updatePortalHead } from './lib/pwa';
import { PortalDestinationProvider, usePortalDestination } from './PortalDestinationContext';
import { PortalCarePage, PortalCareShell } from '../care/CustomerWorkspacePage';
import PortalVerifyIdentityPage from './pages/PortalVerifyIdentityPage';
import PortalFindPage from './pages/PortalFindPage';
import PortalFamilyPage from './pages/PortalFamilyPage';
import PortalHistoryPage from './pages/PortalHistoryPage';
import PortalDoseHelperPage from './pages/PortalDoseHelperPage';
import { PortalSplash } from './ui/PortalSplash';

function PortalGate({ children }) {
  const { patient, loading } = usePortalAuth();
  const location = useLocation();
  const { href, pharmacy } = usePortalDestination();
  if (loading) {
    const logoUrl = pharmacy?.logo_url
      ? (pharmacy.logo_url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${pharmacy.logo_url}` : pharmacy.logo_url)
      : null;
    return <PortalSplash logoUrl={logoUrl} name={pharmacy?.name} testId="portal-auth-splash" />;
  }
  if (!patient) {
    return <Navigate to={href('login')} replace state={{ from: location }} />;
  }
  return <PortalLayout>{children}</PortalLayout>;
}

function PortalRoutes() {
  const { parsed, pharmacy } = usePortalDestination();
  const { activeMemberId } = usePortalAuth();
  useEffect(() => { updatePortalHead(pharmacy); }, [pharmacy]);
  // The sign-in screen owns its return navigation. A competing Navigate here
  // can overwrite the original deep link as soon as patient state is persisted.
  if (parsed.page === 'login') return <PortalLoginPage />;
  // Public: finding a pharmacy must work before anyone signs in.
  if (parsed.page === 'find') return <PortalFindPage />;
  const pages = { '': PortalDashboardPage, profile: PortalAccountPage, 'edit-profile': PortalProfilePage, prescriptions: PortalPrescriptionsPage,
    refills: PortalRefillsPage, orders: PortalOrdersPage, ai: PortalCarePage, chat: PortalChatPage,
    reminders: PortalRemindersPage, rewards: PortalLoyaltyPage, shop: PortalShopPage, trackers: PortalTrackersPage,
    'adverse-reactions': PortalAdverseReactionsPage, family: PortalFamilyPage, history: PortalHistoryPage,
    'dose-helper': PortalDoseHelperPage,
    bills: PortalCarePage, verification: PortalCarePage, clinical: PortalCarePage,
    'verify-id': PortalVerifyIdentityPage };
  const Page = pages[parsed.page] || PortalDashboardPage;
  // Switching person remounts the page so every screen refetches for them.
  return <PortalGate><PortalCareShell><Page key={`${parsed.page}:${activeMemberId || 'self'}`} /></PortalCareShell></PortalGate>;
}

export default function PortalApp() {
  // Inject PWA metadata + register service worker only while the portal
  // is mounted. Cleanup runs when the user leaves for /login, /dashboard, etc.
  useLayoutEffect(() => {
    const cleanup = installHeadTags();
    registerPortalServiceWorker();
    return () => cleanup();
  }, []);

  return (
    <PortalThemeProvider>
      <PortalAuthProvider>
        <PortalDestinationProvider><PortalRoutes /></PortalDestinationProvider>
        <Toaster position="top-center" richColors />
      </PortalAuthProvider>
    </PortalThemeProvider>
  );
}
